import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import HoldAndWinOverlaySymbolController from "./HoldAndWinOverlaySymbolController";
import { safeInit } from "../../utilities";
import {
  selectHoldAndWinCurrentRound,
  selectIsHoldAndWinCompletionReload,
  selectIntroPopupActive,
  selectHoldAndWinResumePopupActive,
} from "../../State/selectors";

/**
 * HoldAndWinOverlaySymbol - Container for spine animations in Hold & Win feature
 * Each symbol position has its own controller that manages ONE coin spine
 */
export const HoldAndWinOverlaySymbol = forwardRef(
  ({ index, reelIndex, y, symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);
    const [controllerReady, setControllerReady] = useState(false); // State to trigger effects when controller is ready
    const previousMultiplierRef = useRef(null); // Track previous multiplier to detect changes
    const wasCompletionReloadRef = useRef(false); // Track if we were ever in completion reload mode
    const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
    const isHoldAndWinCompletionReload = useSelector(selectIsHoldAndWinCompletionReload);
    const introPopupActive = useSelector(selectIntroPopupActive);
    const holdAndWinResumePopupActive = useSelector(selectHoldAndWinResumePopupActive);

    // Memoize container name
    const containerName = useMemo(
      () => `hold_and_win_overlay_symbol_${reelIndex}_${index}`,
      [reelIndex, index]
    );

    // Memoize shouldPlaySound calculation
    const shouldPlaySound = useMemo(
      () => !introPopupActive && !holdAndWinResumePopupActive,
      [introPopupActive, holdAndWinResumePopupActive]
    );

    // Initialize controller once and trigger idle animation for existing coins
    useEffect(() => {
      if (!controllerRef.current && containerRef.current) {
        safeInit(() => {
          controllerRef.current = new HoldAndWinOverlaySymbolController(
            containerRef.current,
            reelIndex,
            index,
            symbolWidth,
            symbolHeight
          );
          setControllerReady(true); // Trigger effects that depend on controller
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Handle resize
    useEffect(() => {
      if (controllerRef.current) {
        controllerRef.current.resizeAnimations(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    // Watch for multiplier changes and trigger display (updates during spins)
    useEffect(() => {
      if (!controllerRef.current) {
        return;
      }

      // CRITICAL: Remember if we were ever in completion reload mode
      // Once set, multipliers should never animate (even after flag is cleared)
      if (isHoldAndWinCompletionReload) {
        wasCompletionReloadRef.current = true;
        controllerRef.current.hideMultiplier();
        return;
      }

      // CRITICAL: If we were in completion reload mode, never show multipliers
      // This prevents multipliers from animating when outro closes and flag is cleared
      if (wasCompletionReloadRef.current) {
        controllerRef.current.hideMultiplier();
        return;
      }

      if (!holdAndWinCurrentRound?.storage?.coin_multipliers) {
        return;
      }
      if (!holdAndWinCurrentRound?.reels) {
        return;
      }

      // Get current multiplier value for this position
      const multiplierValue = holdAndWinCurrentRound.storage.coin_multipliers?.[reelIndex]?.[index];

      // Check if multiplier actually changed
      const multiplierChanged = previousMultiplierRef.current !== multiplierValue;

      if (!multiplierChanged) {
        return;
      }

      // Update ref with current value
      previousMultiplierRef.current = multiplierValue;

      // Show multiplier if value is 2, 3, or 10
      if (multiplierValue && [2, 3, 10].includes(Number(multiplierValue))) {
        // Don't play sound while popups are active (intro popup or reload dialog)
        controllerRef.current.showMultiplier(Number(multiplierValue), shouldPlaySound);
      } else {
        // Hide multiplier if value is 1 or undefined
        controllerRef.current.hideMultiplier();
      }
    }, [holdAndWinCurrentRound, reelIndex, index, controllerReady, isHoldAndWinCompletionReload, shouldPlaySound]);

    // Memoize controller methods
    const getController = useCallback(() => {
      return controllerRef.current;
    }, []);

    const showLandingAnimation = useCallback((symbolId) => {
      controllerRef.current?.showLandingAnimation(symbolId);
    }, []);

    const showWinAnimation = useCallback((symbolId, loop = true, showText = true) => {
      controllerRef.current?.showWinAnimation(symbolId, loop, showText);
    }, []);

    const showDarkAnimation = useCallback((symbolId) => {
      controllerRef.current?.showDarkAnimation(symbolId);
    }, []);

    const hideAnimation = useCallback(() => {
      controllerRef.current?.hideAnimation();
    }, []);

    const fadeCoinValueText = useCallback(() => {
      controllerRef.current?.fadeCoinValueText();
    }, []);

    const applyMultiplierAndAnimate = useCallback((multiplierValue) => {
      return controllerRef.current?.applyMultiplierAndAnimate(multiplierValue);
    }, []);

    // Expose controller methods
    useImperativeHandle(ref, () => ({
      getController,
      showLandingAnimation,
      showWinAnimation,
      showDarkAnimation,
      hideAnimation,
      fadeCoinValueText,
      applyMultiplierAndAnimate,
    }), [getController, showLandingAnimation, showWinAnimation, showDarkAnimation, hideAnimation, fadeCoinValueText, applyMultiplierAndAnimate]);

    return (
      <Container
        ref={containerRef}
        y={y}
        x={0}
        zIndex={7}
        sortableChildren
        name={containerName}
      />
    );
  }
);

export default HoldAndWinOverlaySymbol;
